import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, memo } from "react"
import Loader from "./Loader"
import { formatISODate } from "../utils/datetime"

const columnHelper = createColumnHelper()

const SingleAuditLogsTable = ({
  data,
  loading,
  onPaginationChange,
  pagination,
  pageCount,
  filters,
  setFilters,
}) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        header: "DateTime",
        id: "timestamp",
        size: 100,
        cell: (info) =>
          formatISODate(info.renderValue(), "MM/dd/yyyy hh:mm:ss a") || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("AuditEventMappings.eventDesc", {
        header: "Event Type",
        id: "eventType",
        size: 150,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("numericValue", {
        header: "Tokens Used",
        id: "tokensUsed",
        size: 50,
        cell: (info) => (
          <div className="ml-6">
            {info.renderValue() || <span className="ml-3">-</span>}
          </div>
        ),
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("description", {
        header: "Description",
        id: "description",
        size: 100,
        cell: (info) => info.renderValue() || <span className="ml-8">-</span>,
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
    ],
    []
  )

  const tableConfig = useMemo(
    () => ({
      columns,
      data,
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange,
      onColumnFiltersChange: setFilters,
      pageCount,
      state: {
        pagination,
        columnFilters: filters,
      },
    }),
    [
      columns,
      data,
      onPaginationChange,
      setFilters,
      pageCount,
      pagination,
      filters,
    ]
  )

  const table = useReactTable(tableConfig)

  const TableHeader = useMemo(
    () => (
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((column) => (
              <th
                key={column.id}
                className="p-4 border-b border-gray-200 bg-gray-100 align-top whitespace"
                style={{ width: `${column.getSize()}px` }}
              >
                <div className="flex flex-col justify-start items-start">
                  <button
                    {...{
                      className: column.column.getCanSort()
                        ? "w-full select-none cursor-pointer flex items-center gap-1"
                        : "w-full",
                      onClick: column.column.getToggleSortingHandler(),
                    }}
                  >
                    {flexRender(
                      column.column.columnDef.header,
                      column.getContext()
                    )}
                    {{
                      asc: <SortAscending className="h-4 w-4" />,
                      desc: <SortDescending className="h-4 w-4" />,
                    }[column.column.getIsSorted()] ?? null}
                  </button>
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
    ),
    [table]
  )

  const TableBody = useMemo(() => {
    if (loading) {
      return (
        <tbody>
          <tr>
            <td colSpan={table.getAllColumns().length}>
              <div className="py-2">
                <Loader />
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    return (
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-100">
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="px-4 py-2 border-b border-gray-200">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    )
  }, [loading, table, data])

  return (
    <>
      <div className="p-4">
        <table className="min-w-full bg-white border border-gray-200">
          {TableHeader}
          {TableBody}
        </table>
        {!loading && data.length === 0 && (
          <div className="mt-3 text-center">No Logs found</div>
        )}
      </div>
    </>
  )
}

export default memo(SingleAuditLogsTable)
